import { Modal } from '../../../../components/Modal/Modal'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Collapse, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetAdditionalObjectsQuery, useLazyGetIntegrationUsersQuery, useSaveMeetingMutation } from '../../../../store/integrations/integrations'
import { useLazyGetRoomQuery, useLazyGetRoomSessionQuery, useListAiResultsQuery } from '../../../../store/media/media'

import { formatDateHM } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { WealthboxIntegrationRow } from '../../../ProfileSettings/Integrations/Wealthbox/WealthboxIntegrationRow'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { RoomSession } from '../../../../models/Room'
import { ContactFormSelectDropdown } from '../ContactDropdown'
import { SalesforceIntegrationRow } from '../../../ProfileSettings/Integrations/Salesforce/SalesforceIntegrationRow'
import { MeetingTemplate } from '../../../../store/ai/meetingTemplate'
import { SourceIcon } from '../../../../apps/professional-app/fa/PastMeetings/SourceIcon'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { WritableLexicalEditor } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { useLazyGetPresetsQuery } from '../../../../store/ai/ai'
import { getOrderedMeetingTemplatePresets, getPresetResultsByInputParameterHierarchy } from '../../../../apps/professional-app/fa/MeetingSummary/utils'
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LEXICAL_NODES } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { selectSessionMeetingTemplate } from '../../../../store/meetingSummary/meetingTemplates'
import { selectAiFeatures } from '../../../../store/meetingSummary/aiFeatures'
import { RichTextToolbarPlugin } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { copyResult, HTML_TYPE } from '../../../../apps/professional-app/fa/MeetingSummary/copy'
import { useLazyGetInputParametersHierarchyNodeByPresetsQuery } from '../../../../store/ai/inputParametersHierarchy'
import { InputParametersHierarchyNodeByPresets } from '../../../../models/InputParametersHierarchy'
import { getContactOptions } from '../utils'
import { selectMeetingInfoHTML, setRoomSession } from '../../../../store/meetingSummary/roomSession'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../models/Account'
import { getEditorTextContent } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { User } from '../../../../models/User'
import { useCookies } from 'react-cookie'
import { useIntegrationExportConfirmation } from '../../../../components/ConfirmationDialog/useIntegrationExportConfirmation'
import { selectGroupedTasks } from '../../../../store/task/task'
import { UserSelectDropdown } from '../UserDropdown'
import { TeamSelectDropdown } from '../TeamDropdown'
import { defaultCrmMeetingExportObject } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/CRMIntegrationSettings'
import { useSyncStatuses } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { IntegrationSavedModalBody } from '../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { parseMetadata } from '../../../../common/util'
import { useEffectivePlatform } from '../../../../common/useEffectivePlatform'
import { addToast } from '../../../../store/toast/toastSlice'

interface PreSelectedPresetEnvMapping {
  prod: string[]
  staging: string[]
  dev: string[]
}

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]
const DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION = 'note'
const PRESELECTED_PRESETS: PreSelectedPresetEnvMapping = {
  dev: ['eiaibj6g6uuxmfbu', '3q77qfpmo76ekpp9', '9xbrgp80b4ls6qbx'],
  staging: ['eudwatduwtujes7r', '37wou7icth9r3ymd', '9xbrgp80b4ls6qbx'],
  prod: ['2tspyef3f59unqyl', 'dshudbybhopw2w7h', 'cqkkd1xbz240b6b7'],
}
const env = import.meta.env.REACT_APP_ENV! as 'prod' | 'staging' | 'dev'

interface CRMSessionObjectMapping {
  [key: string]: string
}

const CRM_SESSION_OBJECT: CRMSessionObjectMapping = {
  wealthbox: 'events',
  redtail: 'activities',
  salesforce: 'Event',
  practifi: 'Event',
  xlr8: 'Event',
  zoho: 'Events',
  elements: 'Event',
  blackdiamond: 'Event',
}

type MappingConfig = {
  path: string
  idKey: string
  labelKey: string
  household: HouseholdEventConfig | undefined
}

type HouseholdEventConfig = {
  householdPath: string
  eventPath: string
}

const eventConfigMap: Record<string, MappingConfig> = {
  wealthbox: { path: 'events', idKey: 'id', labelKey: 'title', household: undefined },
  redtail: { path: 'activities', idKey: 'id', labelKey: 'subject', household: undefined },
  salesforce: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  practifi: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  xlr8: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  zoho: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  elements: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  blackdiamond: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
}

type NestedRecord = Record<string, unknown> & {
  [key: string]: NestedRecord | unknown[] | unknown
}

const extractEvents = (data: NestedRecord, config: MappingConfig): { id: string; label: string }[] => {
  const contactCollection = data?.[config.path]
  let contactEvents: { id: string; label: string }[] = []
  if (Array.isArray(contactCollection)) {
    contactEvents = contactCollection.map((item: Record<string, unknown>) => ({
      id: `${item[config.idKey]}`,
      label: `${item[config.labelKey]}`,
    }))
  }

  let householdEvents: { id: string; label: string }[] = []
  if (config.household) {
    const householdData = data as Record<string, unknown>
    const householdContainer = householdData[config.household.householdPath]

    if (householdContainer && typeof householdContainer === 'object') {
      const householdObj = householdContainer as Record<string, unknown>
      const householdCollection = householdObj[config.household.eventPath]

      if (Array.isArray(householdCollection)) {
        householdEvents = householdCollection.map((item: Record<string, unknown>) => ({
          id: `${item[config.idKey]}`,
          label: `${item[config.labelKey]}`,
        }))
      }
    }
  }

  return [...contactEvents, ...householdEvents].filter((event, index, self) => self.findIndex((e) => e.id === event.id) === index)
}

export interface AddMeetingToCRMModalProps {
  roomSession: RoomSession
  meetingTemplate?: MeetingTemplate
  show: boolean
  onHide: () => void
  serviceId: string
  body?: React.ReactNode
}

type FormValues = {
  externalUserId: string
  meetingDescription: string
  externalMeetingId: string | null
  notifyUserId: string | null
  notifyTeamId: string | null
}

const EVENT_DATE_OFFSET = 7 * 24 * 60 * 60 * 1000

export const AddMeetingToCRMModal: React.FC<AddMeetingToCRMModalProps> = ({ show, onHide, roomSession, meetingTemplate, serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useIntegrationExportConfirmation({ roomSession })
  const { tracked } = useTracking()
  const [triggerSaveMeeting, { isLoading }] = useSaveMeetingMutation()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)
  const { data: account } = useGetAccountQuery({ accountId })
  const [triggerGetRoomSession, { isLoading: isGetRoomSessionLoading }] = useLazyGetRoomSessionQuery()
  const [isEventAppendSupported, setIsEventAppendSupported] = useState<boolean>(false)
  const [isNotifySupported, setIsNotifySupported] = useState<boolean>(false)
  const [sessionLabel, setSessionLabel] = useState<string>(DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION)
  const [contactObject, setContactObject] = useState<string>('Contact')
  const [savedMeetingResult, setSavedMeetingResult] = useState<boolean>(false)
  const { roomSessionSyncStatus } = useSyncStatuses()
  useEffect(() => {
    if (!account || !account.data) {
      return
    }
    const serviceIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
    if (!serviceIntegration) {
      return
    }

    const sessionMapping =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].objectName
    const contactObject =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT].objectName
    setSessionLabel(sessionMapping.charAt(0).toUpperCase() + sessionMapping.slice(1))

    const updatable =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? null
        : Object.keys(serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].additionalInformation || {}).includes('updatable')
          ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].additionalInformation?.updatable
          : null

    if (serviceId === 'salesforce') {
      setContactObject(contactObject)
    }

    if (updatable === true || (updatable === null && sessionMapping === CRM_SESSION_OBJECT[serviceId])) {
      setIsEventAppendSupported(true)
    }

    if (sessionMapping === defaultCrmMeetingExportObject['redtail'].id && serviceId === 'redtail') {
      setIsNotifySupported(true)
    }
  }, [account, serviceId])

  const defaultValues = {
    externalUserId: '',
    meetingDescription: '',
    externalMeetingId: '',
  }

  const _getDescription = (originalDescription: string) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    const accountConfigMappingSession = serviceIntegration
      ? typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].objectName
      : DEFAUlT_ACCOUNT_CONFIG_MAPPING_SESSION

    if (accountConfigMappingSession === CRM_SESSION_OBJECT[serviceId]) {
      return originalDescription
    }

    return `<div>${meetingInfoHTML} ${originalDescription}</div>`
  }
  const handleSave = tracked(
    async (data: FormValues) => {
      const description = _getDescription(data.meetingDescription)
      try {
        await runMutationWithConfirmation(async () => {
          const response = await triggerSaveMeeting({
            accountId,
            connector: serviceId,
            body: {
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
              description: description,
              externalUserIds: [data.externalUserId],
              externalMeetingId: data.externalMeetingId,
              ...(data.notifyUserId ? { notifyUserId: data.notifyUserId } : {}),
              ...(data.notifyTeamId ? { notifyTeamId: data.notifyTeamId } : {}),
              relatedTo: {
                id: data.externalUserId,
                type: contactObject,
              },
              exportType: 'export',
            },
          })
          if (!response.error) {
            const { data: newRoomSession } = await triggerGetRoomSession({
              accountId,
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
            })
            if (newRoomSession?.data) {
              dispatch(setRoomSession(newRoomSession.data))
              setSavedMeetingResult(true)
            }
          }
        })
      } catch (e) {
        console.error(e)
      }
    },
    { eventName: TrackEvent.SAVE_MEETING_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )
  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const handleContactSelected = (contact?: User) => {
    if (!contact || !contact.metadata) {
      return
    }
  }

  const handleOnHide = useCallback(() => {
    onHide()
    form.reset()
  }, [onHide, form])

  useEffect(() => {
    if (savedMeetingResult && !roomSessionSyncStatus.length) {
      dispatch(addToast({ variant: 'success', title: 'Success', messages: [`The meeting has been saved to ${serviceId}.`] }))
      handleOnHide()
    }
  }, [savedMeetingResult, roomSessionSyncStatus, handleOnHide, serviceId, dispatch])

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={onHide}
        header={
          !savedMeetingResult ? (
            <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
              Save your meeting to&nbsp;<span className='text-capitalize'>{serviceId}</span>
            </BootstrapModal.Title>
          ) : null
        }
        body={
          savedMeetingResult && roomSessionSyncStatus.length ? (
            <IntegrationSavedModalBody syncStatuses={[roomSessionSyncStatus[0]]} serviceId={serviceId} onHide={handleOnHide} />
          ) : (
            <FormProvider {...form}>
              <AddMeetingToCRMModalBody
                roomSession={roomSession}
                serviceId={serviceId}
                meetingTemplate={meetingTemplate}
                isEventAppendSupported={isEventAppendSupported}
                isNotifySupported={isNotifySupported}
                sessionLabel={sessionLabel}
                onContactSelected={handleContactSelected}
              />
            </FormProvider>
          )
        }
        showFooterActions={!savedMeetingResult}
        onFooterPrimaryAction={handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading || accountIsLoading || isGetRoomSessionLoading}
        onFooterSecondaryAction={onHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

const AddMeetingToCRMModalBody: React.FC<{
  roomSession: RoomSession
  meetingTemplate?: MeetingTemplate
  serviceId: string
  isEventAppendSupported: boolean
  isNotifySupported: boolean
  sessionLabel: string
  onContactSelected?: (contact?: User) => void
}> = ({ roomSession, meetingTemplate, serviceId, isEventAppendSupported, isNotifySupported, sessionLabel, onContactSelected }) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue, watch } = useFormContext<FormValues>()
  const selectedExternalUserId = watch('externalUserId')
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365
  const aiFeatures = useAppSelector(selectAiFeatures)
  const _sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const [sessionMeetingTemplate, setSessionMeetingTemplate] = useState<MeetingTemplate | undefined | null>(meetingTemplate)
  const [inputParametersHierarchyNodes, setInputParametersHierarchyNodes] = useState<InputParametersHierarchyNodeByPresets>()
  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [aiFeaturePresets, setAiFeaturePresets] = useState<AiFeaturePreset[]>([])
  const [triggerGetPresets] = useLazyGetPresetsQuery()
  const [triggerGetRoom] = useLazyGetRoomQuery()
  const [triggerGetInputParametersHierarchyNodeByPresets] = useLazyGetInputParametersHierarchyNodeByPresetsQuery()
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const editorRef = useRef<LexicalEditor>(null)
  const groupedTasks = useAppSelector(selectGroupedTasks)
  const [cookies, setCookie] = useCookies(['defaultCRMExportPresets'])
  const [isModified, setIsModified] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const { trackingClient } = useTracking()

  const { data: aiResults, isFetching: isAIResultsFetching } = useListAiResultsQuery(
    {
      accountId,
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [contacts, setContacts] = useState<User[]>([])
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [selectedPresets, setSelectedPresets] = useState<AiFeaturePreset[]>([])
  const sessionMetadata = parseMetadata(roomSession.metadata)
  const { source, external: { platform = null, userAgent = null } = {} } = sessionMetadata

  const effectivePlatform = useEffectivePlatform(source, platform, userAgent)

  const [eventOptions, setEventOptions] = useState<SelectOption[]>([])

  const { data: additionalObjects, isFetching: isAdditionalObjectsFetching } = useGetAdditionalObjectsQuery(
    {
      accountId,
      connector: serviceId,
      relatedTo: { id: selectedExternalUserId },
      meetingStartDate: new Date(new Date(roomSession.startedAt).getTime() - EVENT_DATE_OFFSET).toISOString(),
      meetingEndDate: new Date(new Date(roomSession.finishedAt ?? roomSession.startedAt).getTime() + EVENT_DATE_OFFSET).toISOString(),
    },
    { skip: !selectedExternalUserId || !isEventAppendSupported },
  )

  useEffect(() => {
    if (_sessionMeetingTemplate && !meetingTemplate) {
      setSessionMeetingTemplate(_sessionMeetingTemplate)
    }
  }, [meetingTemplate, _sessionMeetingTemplate])

  useEffect(() => {
    const getPresetsByIds = async () => {
      try {
        if (!sessionMeetingTemplate) {
          return
        }
        const { data: presetsData } = await triggerGetPresets({
          accountId,
          ids: sessionMeetingTemplate.presets.map((p) => p.aiFeaturePresetId),
          pageRequest: { page: 0, size: 500 },
        })
        if (!presetsData) {
          setAiFeaturePresets([])
          return
        }
        const orderedPresets = getOrderedMeetingTemplatePresets(sessionMeetingTemplate.presets)
        const presets = presetsData.data
        setAiFeaturePresets(
          orderedPresets.map((op) => presets.find((p) => p.id === op.aiFeaturePresetId)).filter((preset): preset is AiFeaturePreset => !!preset),
        )
      } catch (e: unknown) {
        console.error(e)
      }
    }
    getPresetsByIds().catch(console.error)
  }, [accountId, sessionMeetingTemplate, triggerGetPresets])

  useEffect(() => {
    if (!aiFeaturePresets.length || !sessionMeetingTemplate) {
      return
    }
    const aiFeaturePresetIds = aiFeaturePresets.map((preset) => preset.id)

    const getInputParametersHierarchy = async () => {
      const { data: room } = await triggerGetRoom({ accountId, roomId: roomSession.roomId })
      if (!room) {
        return
      }
      const { data: inputParametersHierarchyNodeByPresets } = await triggerGetInputParametersHierarchyNodeByPresets(
        {
          accountId,
          type: 'ai-feature-presets',
          typeIds: aiFeaturePresetIds,
          effective: true,
          meetingTemplateId: sessionMeetingTemplate.id,
          userId: room.createdBy.type === 'user' ? room.createdBy.id : undefined,
        },
        true,
      )
      setInputParametersHierarchyNodes(inputParametersHierarchyNodeByPresets)
    }
    getInputParametersHierarchy().catch(console.error)
  }, [roomSession, aiFeaturePresets, sessionMeetingTemplate, triggerGetInputParametersHierarchyNodeByPresets, triggerGetRoom, accountId])

  useEffect(() => {
    if (!additionalObjects) {
      return
    }
    const config = eventConfigMap[serviceId]
    if (config) {
      setEventOptions(extractEvents(additionalObjects, config))
    }
  }, [additionalObjects, serviceId])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setContacts(contactsData.data)
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const onSelectPreset = (preset: AiFeaturePreset) => {
    if (selectedPresets.map((p) => p.id).some((pid) => pid === preset.id)) {
      setSelectedPresets([...selectedPresets.filter((p) => p.id !== preset.id)])
    } else {
      setSelectedPresets([...selectedPresets, preset])
    }
  }

  const handleOnCancel = () => {
    setSelectedPresets([])
  }

  const handleTextContentChange = () => {
    if (!isModified && isInitialized) {
      setIsModified(true)
      trackingClient.track({
        eventName: TrackEvent.MEETING_EXPORT_EDITED,
        eventDetails: {
          serviceId: serviceId,
        },
      })
    }
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }

  const handleOnChange = (editorState: EditorState, editor: LexicalEditor) => {
    const htmlValue = editorState.read(() => {
      return $generateHtmlFromNodes(editor, null)
    })
    setValue('meetingDescription', htmlValue)
  }

  const handleOnSelectAll = () => {
    setSelectedPresets([...aiFeaturePresets])
  }

  const handleOnAdd = (presets: AiFeaturePreset[] | null = null) => {
    const addText: string[] = []

    const addedPresets = presets || selectedPresets

    const activePresetsIds = addedPresets?.map((preset) => preset.id) || ''
    setCookie('defaultCRMExportPresets', activePresetsIds, { path: '/', maxAge: COOKIE_MAX_AGE })

    for (const preset of addedPresets) {
      const presetAiFeature = aiFeatures.find((f) => f.preset?.id === preset.id)

      if (inputParametersHierarchyNodes && presetAiFeature && aiResults && aiResults.data) {
        const presetResults = getPresetResultsByInputParameterHierarchy(preset.id, presetAiFeature.id, aiResults.data, inputParametersHierarchyNodes).filter(
          (r) => r.status !== 'no_result',
        )

        if (presetResults.length) {
          addText.push(`<h2><u>${presetAiFeature?.name}</u></h2>`)
          addText.push(copyResult(presetAiFeature, presetResults[0], { groupedTasks })[HTML_TYPE])
        }
      }
    }

    const editor = editorRef.current
    if (!editor) {
      return
    }
    const editorIsEmpty = !getEditorTextContent(editor.getEditorState()).trim().length
    editor.update(() => {
      const parser = new DOMParser()
      const text = addText.join('<br />')
      const dom = parser.parseFromString(text, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      $getRoot().select(editorIsEmpty ? 0 : undefined)
      $insertNodes(nodes)
      setValue('meetingDescription', text)
    })

    setSelectedPresets([])
  }

  useEffect(() => {
    if (aiFeaturePresets.length && inputParametersHierarchyNodes && editorRef.current) {
      const previouslySelectedPresetIds = cookies.defaultCRMExportPresets && cookies.defaultCRMExportPresets.length ? cookies.defaultCRMExportPresets : null
      const activePresets = previouslySelectedPresetIds
        ? aiFeaturePresets.filter((p) => previouslySelectedPresetIds.includes(p.id))
        : aiFeaturePresets.filter((p) => PRESELECTED_PRESETS[env].includes(p.id))

      if (activePresets.length) {
        setCollapsed(true)
      }
      handleOnAdd(activePresets)
    }
  }, [editorRef.current, aiFeaturePresets, sessionMeetingTemplate, inputParametersHierarchyNodes])

  const handleContactSelected = (option: SelectOption) => {
    setValue('externalMeetingId', null)
    if (onContactSelected) {
      onContactSelected(
        contacts.find((contact) => {
          const contactIntegration = contact.integrations?.find((i) => i.serviceId === serviceId)
          return contactIntegration?.externalId === option.id
        }),
      )
    }
  }

  if (isAIResultsFetching && !aiResults) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const getConnectorIntegrationRow = () => {
    if (serviceId === 'wealthbox') {
      return <WealthboxIntegrationRow useIntegrationRedirectUri />
    }
    if (serviceId === 'salesforce') {
      return <SalesforceIntegrationRow useIntegrationRedirectUri />
    }
  }

  if (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage))) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid please reconnect your integration:</Col>
          </Row>
        </Container>
        {getConnectorIntegrationRow()}
      </>
    )
  }

  return (
    <Container>
      <Row>
        <Col xs={12} className='crm-meeting-info-header'>
          <Container fluid className='p-0 d-flex align-items-center truncate-long-text'>
            <Row>
              <Col xs='auto'>
                <SourceIcon source={effectivePlatform ? `${source}-${effectivePlatform}` : source} />
              </Col>
              <Col className='d-flex flex-column justify-content-center overflow-hidden'>
                <Row>
                  <Col>
                    <div className='lead-medium'>
                      {formatDateHM(roomSession.startedAt)}-{roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : 'Live'}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h6>{sessionMetadata.external.title ? sessionMetadata.external.title : <div className='paragraph-extra-small'>Untitled meeting.</div>}</h6>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactFormSelectDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
            onOptionSelected={handleContactSelected}
          />
        </Col>
      </Row>
      {isNotifySupported && (
        <div>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify user</div>
              <Controller
                name='notifyUserId'
                control={control}
                render={({ field }) => <UserSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify team</div>
              <Controller
                name='notifyTeamId'
                control={control}
                render={({ field }) => <TeamSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
        </div>
      )}
      {isEventAppendSupported ? (
        <Row className='mt-3'>
          <Col xs={12}>
            <div className='lead-extra-small'>{sessionLabel} (Optional)</div>
            <Controller
              name='externalMeetingId'
              control={control}
              render={({ field }) => {
                const option = field.value ? eventOptions.find((e) => e.id === field.value) : null
                return (
                  <SelectDropdown
                    initialSelectedOption={option ?? null}
                    options={eventOptions}
                    className='py-2 zocks-px-0.75'
                    onOptionSelected={(option) => field.onChange(option.id)}
                    onDeselect={() => field.onChange(null)}
                    placeholder={`Select your ${sessionLabel}`}
                    deselectOptionLabel={`Select your ${sessionLabel}`}
                    loading={isAdditionalObjectsFetching}
                    showDeselectOption={true}
                  />
                )
              }}
            />
          </Col>
        </Row>
      ) : null}
      <Row className='mt-3'>
        <Col>
          <div className='lead-extra-small'>Meeting description</div>
        </Col>
      </Row>
      <Row className='mt-1 gy-3 zocks-bg-color-gray-100 zocks-p-0.75'>
        <Col xs={12} className='pt-0'>
          <div role='button' className='d-flex gap-2' onClick={() => setCollapsed((collapsed) => !collapsed)}>
            <div>Add your results to description</div>
            <i
              className={classNames('bi', {
                'bi-chevron-contract': collapsed,
                'bi-chevron-expand': !collapsed,
              })}
            />
          </div>
          <Collapse in={!collapsed} className='pt-3'>
            <div>
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex gap-2 flex-wrap'>
                  {aiFeaturePresets.map((preset) => {
                    return <SelectablePresetTag key={preset.id} preset={preset} selectedPresets={selectedPresets} onSelect={onSelectPreset} />
                  })}
                  <Button variant='link' className='button-small' onClick={handleOnSelectAll}>
                    Select all
                  </Button>
                </div>
                <div className='d-flex gap-3 justify-content-end'>
                  <Button className='button-small' variant='secondary' onClick={handleOnCancel} disabled={!selectedPresets.length}>
                    Cancel
                  </Button>
                  <Button disabled={!selectedPresets.length} className='button-small' variant='primary' onClick={() => handleOnAdd()}>
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </Collapse>
        </Col>
        <Col>
          <Controller
            name='meetingDescription'
            control={control}
            render={() => (
              <WritableLexicalEditor
                ref={editorRef}
                onChange={handleOnChange}
                onTextContentChange={handleTextContentChange}
                className='flex-column-reverse align-items-start'
                editorContainerClassName='flex-grow-1 add-meeting-to-crm-editor-container'
                containerClassName='w-100'
                lexicalNodes={LEXICAL_NODES}
                header={
                  <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2'>
                    <div className='editable-text-result-rich-text-divider' />
                  </RichTextToolbarPlugin>
                }
              />
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}

interface SelectablePresetTagProps {
  preset: AiFeaturePreset
  selectedPresets: AiFeaturePreset[]
  onSelect: (preset: AiFeaturePreset) => void
}

const SelectablePresetTag: React.FC<SelectablePresetTagProps> = ({ preset, selectedPresets, onSelect }) => {
  const selectedOrder = selectedPresets.map((p) => p.id).findIndex((pid) => pid === preset.id)

  return (
    <div
      role='button'
      className={classNames('d-flex gap-2 align-items-center justify-content-center crm-selectable-preset-tag-container', {
        'crm-selectable-preset-tag-container__selected': selectedOrder >= 0,
      })}
      onClick={() => onSelect(preset)}
    >
      <div>{preset.name}</div>
      <div
        className={classNames('crm-selectable-preset-tag-order-container label-small', {
          'crm-selectable-preset-tag-order-container__selected': selectedOrder >= 0,
        })}
      >
        {selectedOrder >= 0 ? selectedOrder + 1 : null}
      </div>
    </div>
  )
}
